import {
  bindService,
  createServiceToken,
  type AppFeatureDefinition,
  type HttpClient,
  type ServiceBinding,
  type ServiceContainer,
} from "@nasam/kernel";
import {
  assetAssignmentsRepositoryToken,
  cameraRepositoryToken,
  driverAssignmentsRepositoryToken,
  geofencesRepositoryToken,
  poisRepositoryToken,
  trackingRepositoryToken,
  vehicleTrackingStreamToken,
} from "./contracts";
import {
  createAssetAssignmentsHttpRepository,
  createAssetAssignmentsRoutes,
  createMockAssetAssignmentsRepository,
  type AssetAssignmentsRepository,
} from "./features/assetAssignments";
import {
  createDriverAssignmentsHttpRepository,
  createMockDriverAssignmentsRepository,
} from "./features/driverAssignments/data";
import type { DriverAssignmentsRepository } from "./features/driverAssignments/domain";
import {
  createCameraHttpRepository,
  createMockCameraRepository,
} from "./features/assetCameras/data";
import {
  createMockTrackingRepository,
  createMockVehicleTrackingStream,
  createSocketTrackingStream,
  createTrackingHttpRepository,
} from "./features/tracking/data";
import {
  alertHistoryRepositoryToken,
  assetsRepositoryToken,
  branchesRepositoryToken,
  companiesRepositoryToken,
  devicesRepositoryToken,
  gprsCommandsRepositoryToken,
  type AlertHistoryRepository,
  type AssetsRepository,
  type BranchesRepository,
  type CompaniesRepository,
  type DevicesRepository,
  type GprsCommandsRepository,
} from "@nasam/core/contracts";
import type { TrackingGprsServices } from "./features/tracking/presentation";
import {
  createSupervisorAssignmentsHttpRepository,
  createSupervisorAssignmentsMockRepository,
  createSupervisorAssignmentsRoutes,
  type SupervisorAssignmentsRepository,
} from "./features/supervisorAssignments";
import {
  MOCK_POIS,
  createMockPoisRepository,
  createPoisHttpRepository,
  createPoisRoutes,
  type PoisRepository,
} from "./features/pois";
import {
  MOCK_GEOFENCES,
  createGeofencesRoutes,
  createMockGeofencesRepository,
} from "./features/geofences";
import { createFleetMonitoringRoutes } from "./presentation/routes";
import {
  ASSET_ASSIGNMENTS_VIEW,
  ASSET_DRIVER_ASSIGNMENTS_VIEW,
  LIVE_TRACKING_MAP_VIEW,
  POI_VIEW,
  SUPERVISOR_ASSIGNMENTS_VIEW,
} from "./permissions";

export const supervisorAssignmentsRepositoryToken =
  createServiceToken<SupervisorAssignmentsRepository>(
    "fleetMonitoring.supervisorAssignments.repository",
  );

const CRUD_ACTIONS = [
  { id: "view", labelKey: "fleetMonitoring:actions.view" },
  { id: "create", labelKey: "fleetMonitoring:actions.create" },
  { id: "update", labelKey: "fleetMonitoring:actions.update" },
  { id: "delete", labelKey: "fleetMonitoring:actions.delete" },
];

/** The live feed (protocol v1) and its REST companions (company picker,
 *  playback), or fixtures with VITE_TRACKING_SOURCE=mock. */
function createTrackingServiceBindings(httpClient: HttpClient): readonly ServiceBinding[] {
  const trackingEnv = import.meta.env as Record<string, string | undefined>;
  const useSocket = trackingEnv.VITE_TRACKING_SOURCE !== "mock";
  if (useSocket) {
    const stream = createSocketTrackingStream({
      url:
        trackingEnv.VITE_TRACKING_SOCKET_URL ||
        trackingEnv.VITE_API_BASE_URL ||
        window.location.origin,
      ...(trackingEnv.VITE_TRACKING_SOCKET_PATH
        ? { path: trackingEnv.VITE_TRACKING_SOCKET_PATH }
        : {}),
    });
    return [
      bindService(
        trackingRepositoryToken,
        createTrackingHttpRepository(httpClient),
      ),
      bindService(vehicleTrackingStreamToken, stream),
      bindService(cameraRepositoryToken, createCameraHttpRepository(httpClient)),
    ];
  }
  return [
    bindService(trackingRepositoryToken, createMockTrackingRepository()),
    bindService(vehicleTrackingStreamToken, createMockVehicleTrackingStream()),
    // A test card per camera: a four-camera BSJ dashcam on most vehicles, a
    // JC400 (cabin and road) on truck-04; truck-09 and car-31 have no camera
    // (`hasCamera: false` in the mock feed).
    bindService(cameraRepositoryToken, createMockCameraRepository({ jc400: ["camera-truck-04"] })),
  ];
}

// Every operation is confirmed real now (NASAM-278, NASAM-279, NASAM-280,
// NASAM-281, NASAM-282, NASAM-773). Typed `boolean` (not inferred as the
// literal `false`) so flipping one by hand doesn't fight
// `no-unnecessary-condition`, same convention as sensorAssignmentsFeature.ts.
// The Branch *filter* is the real `BranchesRepository` below (not toggled
// here at all -- it's a separate, already-shipped contract, resolved
// straight from the registry).
const USE_MOCK_SUPERVISOR_ASSIGNMENT_LIST: boolean = false;
const USE_MOCK_SUPERVISOR_ASSIGNMENT_ELIGIBLE: boolean = false;
const USE_MOCK_SUPERVISOR_ASSIGNMENT_ASSETS: boolean = false;
const USE_MOCK_SUPERVISOR_ASSIGNMENT_DETAIL: boolean = false;
const USE_MOCK_SUPERVISOR_ASSIGNMENT_CREATE: boolean = false;
const USE_MOCK_SUPERVISOR_ASSIGNMENT_UPDATE: boolean = false;
const USE_MOCK_SUPERVISOR_ASSIGNMENT_DELETE: boolean = false;
const USE_MOCK_SUPERVISOR_ASSIGNMENT_HISTORY: boolean = false;

function createSupervisorAssignmentsServiceBindings(
  httpClient: HttpClient,
): readonly ServiceBinding[] {
  const http = createSupervisorAssignmentsHttpRepository(httpClient);
  const mock = createSupervisorAssignmentsMockRepository();

  const repository: SupervisorAssignmentsRepository = {
    list: (query) => (USE_MOCK_SUPERVISOR_ASSIGNMENT_LIST ? mock : http).list(query),
    listEligibleSupervisors: () =>
      (USE_MOCK_SUPERVISOR_ASSIGNMENT_ELIGIBLE ? mock : http).listEligibleSupervisors(),
    listAssignableAssets: (branchId) =>
      (USE_MOCK_SUPERVISOR_ASSIGNMENT_ASSETS ? mock : http).listAssignableAssets(
        branchId,
      ),
    getById: (supervisorId) =>
      (USE_MOCK_SUPERVISOR_ASSIGNMENT_DETAIL ? mock : http).getById(supervisorId),
    create: (input) =>
      (USE_MOCK_SUPERVISOR_ASSIGNMENT_CREATE ? mock : http).create(input),
    update: (input) =>
      (USE_MOCK_SUPERVISOR_ASSIGNMENT_UPDATE ? mock : http).update(input),
    delete: (supervisorId) =>
      (USE_MOCK_SUPERVISOR_ASSIGNMENT_DELETE ? mock : http).delete(supervisorId),
    listHistory: (query) =>
      (USE_MOCK_SUPERVISOR_ASSIGNMENT_HISTORY ? mock : http).listHistory(query),
  };

  return [bindService(supervisorAssignmentsRepositoryToken, repository)];
}

// Per-endpoint switch, like the supervisor ones above: each flips to the real
// API as the backend ships it. Create and its three pickers (NASAM-283) and
// the list (NASAM-284), details (NASAM-779), update (NASAM-285), delete
// (NASAM-286) and history (NASAM-287) are all live.
const USE_MOCK_DRIVER_ASSIGNMENT_LIST: boolean = false;
const USE_MOCK_DRIVER_ASSIGNMENT_DETAIL: boolean = false;
const USE_MOCK_DRIVER_ASSIGNMENT_UPDATE: boolean = false;
const USE_MOCK_DRIVER_ASSIGNMENT_DELETE: boolean = false;
const USE_MOCK_DRIVER_ASSIGNMENT_HISTORY: boolean = false;
const USE_MOCK_DRIVER_ASSIGNMENT_CREATE: boolean = false;
const USE_MOCK_DRIVER_ASSIGNMENT_DRIVERS: boolean = false;
const USE_MOCK_DRIVER_ASSIGNMENT_ASSETS: boolean = false;
const USE_MOCK_DRIVER_ASSIGNMENT_BRANCHES: boolean = false;

function createDriverAssignmentsServiceBindings(
  httpClient: HttpClient,
): readonly ServiceBinding[] {
  const http = createDriverAssignmentsHttpRepository(httpClient);
  const mock = createMockDriverAssignmentsRepository({ latencyMs: 300 });

  const repository: DriverAssignmentsRepository = {
    list: (query) => (USE_MOCK_DRIVER_ASSIGNMENT_LIST ? mock : http).list(query),
    getById: (id) =>
      (USE_MOCK_DRIVER_ASSIGNMENT_DETAIL ? mock : http).getById(id),
    update: (id, input) =>
      (USE_MOCK_DRIVER_ASSIGNMENT_UPDATE ? mock : http).update(id, input),
    delete: (id) => (USE_MOCK_DRIVER_ASSIGNMENT_DELETE ? mock : http).delete(id),
    listHistory: (query) =>
      (USE_MOCK_DRIVER_ASSIGNMENT_HISTORY ? mock : http).listHistory(query),
    create: (input) =>
      (USE_MOCK_DRIVER_ASSIGNMENT_CREATE ? mock : http).create(input),
    listAvailableDrivers: () =>
      (USE_MOCK_DRIVER_ASSIGNMENT_DRIVERS ? mock : http).listAvailableDrivers(),
    listAssignableAssets: (query) =>
      (USE_MOCK_DRIVER_ASSIGNMENT_ASSETS ? mock : http).listAssignableAssets(
        query,
      ),
    listBranches: () =>
      (USE_MOCK_DRIVER_ASSIGNMENT_BRANCHES ? mock : http).listBranches(),
  };

  return [bindService(driverAssignmentsRepositoryToken, repository)];
}

// Per-endpoint switch, like the ones above: the list (NASAM-290) and assign
// (NASAM-289) are both live. The Assign form's Branch and Assets pickers are
// core's own shared repositories, resolved in routes() below.
const USE_MOCK_ASSET_ASSIGNMENT_LIST: boolean = false;
const USE_MOCK_ASSET_ASSIGNMENT_ASSIGN: boolean = false;

function createAssetAssignmentsServiceBindings(
  httpClient: HttpClient,
): readonly ServiceBinding[] {
  const http = createAssetAssignmentsHttpRepository(httpClient);
  const mock = createMockAssetAssignmentsRepository({ latencyMs: 300 });

  const repository: AssetAssignmentsRepository = {
    list: (query) => (USE_MOCK_ASSET_ASSIGNMENT_LIST ? mock : http).list(query),
    assign: (input) =>
      (USE_MOCK_ASSET_ASSIGNMENT_ASSIGN ? mock : http).assign(input),
  };

  return [bindService(assetAssignmentsRepositoryToken, repository)];
}

// Per-endpoint switch, like the ones above: the list (`GET /pois`), the map
// layer (`GET /pois/map`), details (`GET /pois/:poiId`), create
// (`POST /pois`), update (`PATCH /pois/:poiId`), delete
// (`DELETE /pois/:poiId`), the list's Download (`GET /pois/export`) and Bulk
// Upload's template, preview, import and
// error report (`GET /pois/bulk-upload/template`, `POST /pois/bulk-upload/
// preview`, `POST /pois/bulk-upload`, `POST /pois/bulk-upload/error-report`)
// are all live.
const USE_MOCK_POI_LIST: boolean = false;
const USE_MOCK_POI_MAP: boolean = false;
const USE_MOCK_POI_CREATE: boolean = false;
const USE_MOCK_POI_UPDATE: boolean = false;
const USE_MOCK_POI_DETAIL: boolean = false;
const USE_MOCK_POI_DELETE: boolean = false;
const USE_MOCK_POI_TEMPLATE: boolean = false;
const USE_MOCK_POI_EXPORT: boolean = false;
const USE_MOCK_POI_BULK_UPLOAD: boolean = false;

function createPoisServiceBindings(httpClient: HttpClient): readonly ServiceBinding[] {
  const http = createPoisHttpRepository(httpClient);
  const mock = createMockPoisRepository({ pois: MOCK_POIS, latencyMs: 300 });

  const repository: PoisRepository = {
    list: (query) => (USE_MOCK_POI_LIST ? mock : http).list(query),
    listForMap: (query) => (USE_MOCK_POI_MAP ? mock : http).listForMap(query),
    getById: (id) => (USE_MOCK_POI_DETAIL ? mock : http).getById(id),
    create: (input) => (USE_MOCK_POI_CREATE ? mock : http).create(input),
    update: (id, input) =>
      (USE_MOCK_POI_UPDATE ? mock : http).update(id, input),
    delete: (id) => (USE_MOCK_POI_DELETE ? mock : http).delete(id),
    downloadTemplate: () =>
      (USE_MOCK_POI_TEMPLATE ? mock : http).downloadTemplate(),
    exportPois: (query) => (USE_MOCK_POI_EXPORT ? mock : http).exportPois(query),
    previewBulkUpload: (file) =>
      (USE_MOCK_POI_BULK_UPLOAD ? mock : http).previewBulkUpload(file),
    bulkUpload: (file) =>
      (USE_MOCK_POI_BULK_UPLOAD ? mock : http).bulkUpload(file),
    // With the preview: the report lists the same rows it refused.
    downloadBulkErrorReport: (file) =>
      (USE_MOCK_POI_BULK_UPLOAD ? mock : http).downloadBulkErrorReport(file),
  };

  return [bindService(poisRepositoryToken, repository)];
}

// TEMPORARY: sample data only, since the API has no geofences yet.
function createGeofencesServiceBindings(): readonly ServiceBinding[] {
  return [
    bindService(
      geofencesRepositoryToken,
      createMockGeofencesRepository({
        geofences: MOCK_GEOFENCES,
        latencyMs: 300,
      }),
    ),
  ];
}

/** Core's GPRS commands, when the GPRS module is installed: Tracking's Send
 *  GPRS Command opens its Send form. */
function gprsServices(services: ServiceContainer): TrackingGprsServices | undefined {
  const commands = services.tryResolve<GprsCommandsRepository>(gprsCommandsRepositoryToken);
  return commands === undefined
    ? undefined
    : {
        commands,
        devices: services.tryResolve<DevicesRepository>(devicesRepositoryToken),
        companies: services.tryResolve<CompaniesRepository>(companiesRepositoryToken),
      };
}

export const fleetMonitoringFeature: AppFeatureDefinition = {
  id: "fleet-monitoring",
  version: "0.1.0",
  titleKey: "common:businessModules.fleetMonitoring",
  icon: "PointOnMap",
  basePath: "/fleet",
  requiredPermissions: [
    SUPERVISOR_ASSIGNMENTS_VIEW,
    ASSET_ASSIGNMENTS_VIEW,
    ASSET_DRIVER_ASSIGNMENTS_VIEW,
    LIVE_TRACKING_MAP_VIEW,
    POI_VIEW,
  ],
  navigation: [
    {
      id: "fleet-monitoring",
      titleKey: "common:businessModules.fleetMonitoring",
      icon: "PointOnMap",
      placement: "rail",
      order: 20,
      children: [
        {
          id: "fleet-assignments",
          titleKey: "fleetMonitoring:nav.assignments",
          placement: "drawer",
          order: 10,
          children: [
            {
              id: "fleet-assignments-supervisors",
              titleKey: "fleetMonitoring:nav.supervisorAssignments",
              placement: "drawer",
              order: 10,
              path: "/fleet/assignments/supervisors",
              requiredPermissions: [SUPERVISOR_ASSIGNMENTS_VIEW],
            },
            {
              id: "fleet-assignments-assets",
              titleKey: "fleetMonitoring:nav.assetAssignments",
              placement: "drawer",
              order: 20,
              path: "/fleet/assignments/assets",
              requiredPermissions: [ASSET_ASSIGNMENTS_VIEW],
            },
            {
              id: "fleet-assignments-asset-drivers",
              titleKey: "fleetMonitoring:nav.assetDriverAssignments",
              placement: "drawer",
              order: 30,
              path: "/fleet/assignments/asset-drivers",
              requiredPermissions: [ASSET_DRIVER_ASSIGNMENTS_VIEW],
            },
          ],
        },
        {
          id: "fleet-tracking",
          titleKey: "fleetMonitoring:nav.tracking",
          placement: "drawer",
          order: 20,
          path: "/fleet/tracking",
          requiredPermissions: [LIVE_TRACKING_MAP_VIEW],
          fillHeight: true,
          // The tracking screen's own header carries the bell and language
          // controls (Figma 2963:126027), so the desktop top bar is hidden.
          hideTopBar: true,
        },
        {
          id: "fleet-pois",
          titleKey: "fleetMonitoring:nav.pois",
          placement: "drawer",
          order: 30,
          // The POI screen, and Create POI under it.
          path: "/fleet/pois",
          requiredPermissions: [POI_VIEW],
          // The map fills the rest of the screen, and the side panel carries
          // the header (with the bell and language controls).
          fillHeight: true,
          hideTopBar: true,
        },
        {
          id: "fleet-geofences",
          titleKey: "fleetMonitoring:nav.geofences",
          placement: "drawer",
          order: 40,
          path: "/fleet/geofences",
          // TEMPORARY: no permission until the API has geofences, so the
          // screen can be reviewed. Restore [GEOFENCE_VIEW] then.
          // Laid out like the POI screen: the map fills the rest of the
          // screen, and the side panel carries the header.
          fillHeight: true,
          hideTopBar: true,
        },
      ],
    },
  ],
  routes: (context) => [
    ...createFleetMonitoringRoutes({
      context,
      trackingRepository: context.services.resolve(trackingRepositoryToken),
      trackingStream: context.services.resolve(vehicleTrackingStreamToken),
      driverAssignmentsRepository: context.services.resolve(
        driverAssignmentsRepositoryToken,
      ),
      alertHistory: context.services.tryResolve<AlertHistoryRepository>(
        alertHistoryRepositoryToken,
      ),
      cameras: context.services.resolve(cameraRepositoryToken),
      gprs: gprsServices(context.services),
    }),
    ...createSupervisorAssignmentsRoutes({
      context,
      basePath: `${context.basePath}/assignments/supervisors`,
      supervisorAssignments: context.services.resolve(
        supervisorAssignmentsRepositoryToken,
      ),
      branches: context.services.tryResolve<BranchesRepository>(
        branchesRepositoryToken,
      ),
    }),
    ...createAssetAssignmentsRoutes({
      context,
      basePath: `${context.basePath}/assignments/assets`,
      assetAssignments: context.services.resolve(assetAssignmentsRepositoryToken),
      branches: context.services.tryResolve<BranchesRepository>(
        branchesRepositoryToken,
      ),
      assets: context.services.tryResolve<AssetsRepository>(assetsRepositoryToken),
    }),
    ...createPoisRoutes({
      context,
      basePath: `${context.basePath}/pois`,
      pois: context.services.resolve(poisRepositoryToken),
    }),
    ...createGeofencesRoutes({
      context,
      basePath: `${context.basePath}/geofences`,
      geofences: context.services.resolve(geofencesRepositoryToken),
    }),
  ],
  provideServices: ({ httpClient }) => [
    ...createTrackingServiceBindings(httpClient),
    ...createPoisServiceBindings(httpClient),
    ...createGeofencesServiceBindings(),
    ...createAssetAssignmentsServiceBindings(httpClient),
    ...createDriverAssignmentsServiceBindings(httpClient),
    ...createSupervisorAssignmentsServiceBindings(httpClient),
  ],
  i18n: {
    namespace: "fleetMonitoring",
    load: {
      en: () => import("./i18n/en"),
      ar: () => import("./i18n/ar"),
    },
  },
  permissionCatalog: [
    {
      segment: "fleet_monitoring",
      labelKey: "fleetMonitoring:permissions.fleetMonitoring",
      children: [
        {
          segment: "assignment_management",
          labelKey: "fleetMonitoring:permissions.assignmentManagement",
          children: [
            {
              segment: "supervisor_assignment_list",
              labelKey: "fleetMonitoring:permissions.supervisorAssignments",
              actions: CRUD_ACTIONS,
            },
            {
              segment: "asset_assignment_list",
              labelKey: "fleetMonitoring:permissions.assetAssignments",
              actions: CRUD_ACTIONS,
            },
            {
              segment: "driver_assignment_list",
              labelKey: "fleetMonitoring:permissions.driverAssignments",
              actions: CRUD_ACTIONS,
            },
            {
              segment: "asset_driver_assignment_list",
              labelKey: "fleetMonitoring:permissions.assetDriverAssignments",
              actions: CRUD_ACTIONS,
            },
          ],
        },
        {
          segment: "tracking_management",
          labelKey: "fleetMonitoring:permissions.trackingManagement",
          children: [
            {
              segment: "live_tracking_map",
              labelKey: "fleetMonitoring:permissions.liveTrackingMap",
              actions: [
                { id: "view", labelKey: "fleetMonitoring:actions.view" },
              ],
            },
            {
              // Watching a vehicle's dashcams, and talking to their speaker:
              // talk has no permission of its own.
              segment: "live_video",
              labelKey: "fleetMonitoring:permissions.liveVideo",
              actions: [
                { id: "view", labelKey: "fleetMonitoring:actions.view" },
              ],
            },
            {
              // A dashcam's recordings, beside map playback.
              segment: "video_playback",
              labelKey: "fleetMonitoring:permissions.videoPlayback",
              actions: [
                { id: "view", labelKey: "fleetMonitoring:actions.view" },
              ],
            },
            {
              // Hearing the cab on live video (the stream carries sound).
              segment: "video_listen",
              labelKey: "fleetMonitoring:permissions.videoListen",
              actions: [
                { id: "view", labelKey: "fleetMonitoring:actions.view" },
              ],
            },
            {
              // Points of interest.
              segment: "poi_management_list",
              labelKey: "fleetMonitoring:permissions.pois",
              actions: CRUD_ACTIONS,
            },
            {
              segment: "geofence_management_list",
              labelKey: "fleetMonitoring:permissions.geofences",
              actions: CRUD_ACTIONS,
            },
          ],
        },
      ],
    },
  ],
};
